import { useEffect, useRef } from "react";

import { AnimatePresence, motion } from "framer-motion";

const formatSubscriptionType = (value) => {
  if (!value) return "Paid recurring";

  return value.charAt(0).toUpperCase() + value.slice(1);
};

const PlaylistTransferConfirmationModal = ({
  destinationLabel,
  isLoading = false,
  onCancel,
  onConfirm,
  transfer,
}) => {
  const isOpen = Boolean(transfer);
  const hasConfirmationToken = Boolean(transfer?.confirmationToken);
  const transfersAccountData = transfer?.transferMode === "account_data";
  const cancelButtonRef = useRef(null);
  const dialogRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;

    const previouslyFocused = document.activeElement;
    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";
    cancelButtonRef.current?.focus();

    return () => {
      document.body.style.overflow = previousOverflow;
      if (previouslyFocused instanceof HTMLElement) previouslyFocused.focus();
    };
  }, [isOpen]);

  const handleDialogKeyDown = (event) => {
    if (event.key === "Escape" && !isLoading) {
      event.preventDefault();
      event.stopPropagation();
      onCancel();

      return;
    }

    if (event.key !== "Tab") return;

    const focusable = Array.from(
      dialogRef.current?.querySelectorAll("button:not(:disabled)") || []
    );

    if (focusable.length === 0) {
      event.preventDefault();

      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div
          className="fixed inset-0 flex items-center justify-center bg-black/60 px-4 backdrop-blur-md"
          style={{ zIndex: 100000 }}
        >
          <motion.div
            animate={{ opacity: 1, scale: 1, y: 0 }}
            aria-busy={isLoading}
            aria-describedby="playlist-transfer-description"
            aria-labelledby="playlist-transfer-title"
            aria-modal="true"
            className="relative max-h-[calc(100vh-2rem)] w-full max-w-lg overflow-x-hidden overflow-y-auto rounded-2xl bg-white shadow-2xl ring-1 ring-gray-200 dark:bg-gradient-to-b dark:from-gray-900 dark:to-[#0b0f1a] dark:ring-white/10"
            exit={{ opacity: 0, scale: 0.96, y: 10 }}
            initial={{ opacity: 0, scale: 0.96, y: 10 }}
            ref={dialogRef}
            role="dialog"
            tabIndex={-1}
            transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
            onKeyDown={handleDialogKeyDown}
          >
            <div
              aria-hidden
              className="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full bg-amber-400/15 blur-3xl"
            />

            <div className="relative px-6 pb-5 pt-6">
              <div className="mb-4 flex items-start gap-4">
                <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-amber-100 text-amber-600 ring-1 ring-amber-500/20 dark:bg-amber-500/15 dark:text-amber-400">
                  <i className="fas fa-triangle-exclamation text-lg" />
                </div>
                <div className="min-w-0">
                  <p className="mb-1 text-[10px] font-bold uppercase tracking-[0.2em] text-amber-600 dark:text-amber-300">
                    Lunova account transfer warning
                  </p>
                  <h3
                    className="text-xl font-bold tracking-tight text-gray-900 dark:text-white"
                    id="playlist-transfer-title"
                  >
                    Transfer this playlist?
                  </h3>
                </div>
              </div>

              <p
                className="text-sm leading-6 text-gray-600 dark:text-gray-300"
                id="playlist-transfer-description"
              >
                These verified playlist credentials are associated with another
                Lunova account and an active subscription.
              </p>

              <dl className="mt-4 overflow-hidden rounded-xl border border-gray-200 bg-gray-50 text-sm dark:border-white/10 dark:bg-black/20">
                <div className="grid grid-cols-[7rem_1fr] gap-3 border-b border-gray-200 px-4 py-3 dark:border-white/10">
                  <dt className="text-gray-500 dark:text-gray-400">Email</dt>
                  <dd className="break-all font-semibold text-gray-900 dark:text-white">
                    {transfer.associatedEmail || "Unavailable"}
                  </dd>
                </div>
                <div className="grid grid-cols-[7rem_1fr] gap-3 border-b border-gray-200 px-4 py-3 dark:border-white/10">
                  <dt className="text-gray-500 dark:text-gray-400">Username</dt>
                  <dd className="break-all font-semibold text-gray-900 dark:text-white">
                    {transfer.username}
                  </dd>
                </div>
                <div className="grid grid-cols-[7rem_1fr] gap-3 border-b border-gray-200 px-4 py-3 dark:border-white/10">
                  <dt className="text-gray-500 dark:text-gray-400">
                    Destination
                  </dt>
                  <dd className="break-words font-semibold text-gray-900 dark:text-white">
                    {destinationLabel || "New account"}
                  </dd>
                </div>
                <div className="grid grid-cols-[7rem_1fr] gap-3 px-4 py-3">
                  <dt className="text-gray-500 dark:text-gray-400">
                    Subscription
                  </dt>
                  <dd className="font-semibold text-gray-900 dark:text-white">
                    {formatSubscriptionType(transfer.subscriptionType)}
                  </dd>
                </div>
              </dl>

              <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50/80 p-4 text-sm leading-6 text-gray-700 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-gray-200">
                <p className="text-xs font-semibold uppercase tracking-wider text-amber-700 dark:text-amber-300">
                  Important: this action changes both accounts
                </p>
                {transfersAccountData ? (
                  <p className="mt-2">
                    This is the other account&apos;s only playlist. The
                    playlist, profiles, history, favorites, and related account
                    data will move to this account.
                  </p>
                ) : (
                  <p className="mt-2">
                    The other account has {transfer.sourcePlaylistCount}{" "}
                    playlists. Only this playlist will move. Its profiles,
                    history, other playlists, and account data will stay on the
                    other account.
                  </p>
                )}
                <p className="mt-2">
                  The claimed LunaKey on the other account will be canceled and
                  replaced with a new unclaimed LunaKey. Its subscription and
                  remaining paid time will stay unchanged.
                </p>
                {!hasConfirmationToken && (
                  <p className="mt-2 font-semibold text-red-600 dark:text-red-300">
                    This confirmation is unavailable. Cancel and review the
                    playlist again.
                  </p>
                )}
              </div>
            </div>

            <div className="sticky bottom-0 z-10 flex flex-col-reverse gap-2 border-t border-gray-100 bg-gray-50/95 px-6 py-4 backdrop-blur-md dark:border-white/10 dark:bg-gray-900/95 sm:flex-row sm:justify-end">
              <button
                className="w-full rounded-full px-5 py-2.5 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50 dark:text-gray-300 dark:hover:bg-white/5 sm:w-auto"
                disabled={isLoading}
                ref={cancelButtonRef}
                type="button"
                onClick={onCancel}
              >
                Cancel
              </button>
              <button
                className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-amber-500 px-5 py-2.5 text-sm font-semibold text-gray-950 shadow-lg shadow-amber-500/20 transition-all hover:bg-amber-400 active:scale-95 disabled:cursor-not-allowed disabled:opacity-50 disabled:shadow-none sm:w-auto"
                disabled={isLoading || !hasConfirmationToken}
                type="button"
                onClick={onConfirm}
              >
                {isLoading ? (
                  <>
                    <i className="fas fa-spinner fa-spin text-xs" />
                    Transferring...
                  </>
                ) : (
                  <>
                    Transfer playlist
                    <i className="fas fa-arrow-right text-xs" />
                  </>
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default PlaylistTransferConfirmationModal;
